/**
 * Bounded shape for logging a save failure without leaking the request body.
 *
 * An AxiosError carries `config.data` — the full POST body, i.e. the pasted
 * chart text — so `console.error('…', err)` would put PHI wherever the console
 * output is captured or shipped. This pulls out only the fields useful for
 * diagnosing the failure.
 *
 * `name`, `message`, and `stack` are always safe to include: they are
 * runtime/app-authored strings, never derived from the response body. An
 * AxiosError's `message` in particular is a fixed template ("Request failed
 * with status code 400", "timeout of 5000ms exceeded") — it never echoes
 * `response.data` or `config.data`, so it carries the same guarantee for
 * both plain Errors and AxiosErrors. `config` and `request` stay dropped.
 *
 * One module, imported by both patient-save surfaces (ChatContext's two legs
 * and PatientDataInputDialog). It shipped as two byte-identical copies, and a
 * copy is exactly how a PHI guard rots: the next field that has to be dropped
 * gets dropped from the copy the author was looking at.
 */

/**
 * One FastAPI 422 validation error, reduced to the fields that identify WHICH
 * rule refused the request.
 */
interface SafeValidationError {
  loc: unknown
  msg: unknown
  type: unknown
}

interface SaveErrorDescription {
  status: unknown
  code: unknown
  detail: unknown
  name: unknown
  message: unknown
  stack: unknown
}

/**
 * A 422 body is an ARRAY of errors, and FastAPI echoes each error's `input` —
 * the value that failed validation. For a model-level validator that value is
 * the whole request body, i.e. the pasted chart (see #559 for the app-wide
 * gap), so passing `detail` through verbatim would put PHI in the console by
 * the one route that most reliably produces it. Keep loc/msg/type, which say
 * which field and which rule refused, and drop everything else — `input`
 * included — rather than deleting a named key, so a future FastAPI field
 * carrying the value under another name is dropped too.
 *
 * Anything that is not an array (every other refusal on these routes hands back
 * a plain string) passes through unchanged: those are server-authored sentences
 * with no caller text in them.
 */
const safeDetail = (detail: unknown): unknown => {
  if (!Array.isArray(detail)) return detail
  return detail.map((entry): SafeValidationError => {
    const e = entry as { loc?: unknown; msg?: unknown; type?: unknown }
    return { loc: e?.loc, msg: e?.msg, type: e?.type }
  })
}

export const describeSaveError = (err: unknown): SaveErrorDescription => {
  const e = err as {
    response?: { status?: unknown; data?: { detail?: unknown } }
    code?: unknown
    name?: unknown
    message?: unknown
    stack?: unknown
  }
  return {
    status: e?.response?.status,
    code: e?.code,
    detail: safeDetail(e?.response?.data?.detail),
    name: e?.name,
    message: e?.message,
    stack: e?.stack,
  }
}
